import { useState } from 'react';
import type { OEMPartner } from '../../services/oemAdmin';
import type { Tenant } from '../../services/tenancy';
import { DIRECT_LABEL, customerOEMLabel, describeOEMChange, errorText, type OEMRef } from '../oem/oemModel';
import { Dialog } from './OEMPartnersManager';

// Platform administration of a customer's OEM Partner (Settings -> Customers). Changing it moves
// delegated administration only (PUT /api/admin/tenants/{id}/oem-partner); networks, radios,
// groups and operational data stay with the customer.

const select = 'bg-gray-900 border border-gray-600 rounded px-3 py-1.5 text-sm';

/** All / Direct / one OEM Partner (a select, not a tab per OEM). */
export function CustomerOEMFilter({ value, onChange, partners }: { value: string; onChange: (v: string) => void; partners: OEMPartner[] }) {
  return (
    <div>
      <label htmlFor="customer-oem-filter" className="block text-xs text-gray-300 mb-1">OEM Partner</label>
      <select id="customer-oem-filter" value={value} onChange={(e) => onChange(e.target.value)} className={`${select} w-full`}>
        <option value="all">All customers</option>
        <option value="direct">{DIRECT_LABEL}</option>
        {partners.map((p) => <option key={p.id} value={p.id}>{p.name}{p.status === 'disabled' ? ' (disabled)' : ''}</option>)}
      </select>
    </div>
  );
}

export function CustomerOEMCard({ tenant, partners, onChange }: {
  tenant: Pick<Tenant, 'id' | 'name' | 'oem_partner'>; partners: OEMPartner[]; onChange: (oemId: string | null) => Promise<void> | void;
}) {
  const current: OEMRef | null = tenant.oem_partner ? { id: tenant.oem_partner.id, name: tenant.oem_partner.name } : null;
  const [target, setTarget] = useState<string>(current?.id || 'direct');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const next: OEMRef | null = target === 'direct' ? null : partners.filter((p) => p.id === target).map((p) => ({ id: p.id, name: p.name }))[0] || null;
  const change = describeOEMChange(tenant.name, current, next);
  // Only active OEM Partners can receive customers (the server refuses a disabled one with 409).
  const choices = partners.filter((p) => p.status === 'active' || p.id === current?.id);
  return (
    <div className="bg-gray-800 border border-gray-700 rounded p-4 space-y-3">
      <h3 className="font-semibold">OEM Partner</h3>
      <p className="text-sm">Current: <span className="font-medium">{customerOEMLabel(tenant)}</span></p>
      <div className="flex flex-wrap gap-2 items-end">
        <div>
          <label htmlFor={`oem-target-${tenant.id}`} className="block text-xs text-gray-300 mb-1">Change to</label>
          <select id={`oem-target-${tenant.id}`} value={target} onChange={(e) => setTarget(e.target.value)} className={select}>
            <option value="direct">{DIRECT_LABEL}</option>
            {choices.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <button className="px-3 py-1.5 rounded text-sm font-medium bg-blue-600 hover:bg-blue-500 disabled:opacity-40"
          disabled={change.kind === 'none'} onClick={() => { setError(null); setConfirming(true); }}>
          {change.kind === 'none' ? 'Change…' : `${change.confirm}…`}
        </button>
      </div>
      <p className="text-xs text-gray-400">{change.explanation}</p>
      {confirming && <CustomerOEMDialog change={change} busy={busy} error={error} onCancel={() => setConfirming(false)}
        onConfirm={async () => {
          setBusy(true);
          try {
            await onChange(next ? next.id : null);
            setConfirming(false);
          } catch (err) {
            setError(errorText(err));
          } finally {
            setBusy(false);
          }
        }} />}
    </div>
  );
}

export function CustomerOEMDialog({ change, onCancel, onConfirm, busy, error }: {
  change: ReturnType<typeof describeOEMChange>; onCancel: () => void; onConfirm: () => void; busy?: boolean; error?: string | null;
}) {
  return (
    <Dialog title={change.title} onCancel={onCancel} onConfirm={onConfirm} confirm={change.confirm} busy={busy}>
      <dl className="grid grid-cols-3 gap-y-1">
        <dt className="text-gray-400">Customer:</dt><dd className="col-span-2 font-medium">{change.customer}</dd>
        <dt className="text-gray-400">Current:</dt><dd className="col-span-2">{change.current}</dd>
        <dt className="text-gray-400">New:</dt><dd className="col-span-2">{change.next}</dd>
      </dl>
      <p className="text-gray-300">{change.explanation}</p>
      {error && <p role="alert" className="text-red-300">{error}</p>}
    </Dialog>
  );
}
